import { createSlice } from '@reduxjs/toolkit';
import { APIStatus } from '@application/enums/api-status';
import { createVDKEventAsync, getAllVDKEventsAsync, getVDKEventByIdAsync, updateVDKEventAsync, deleteVDKEventAsync, getVDKEventByIdForEventDetailsAsync, validateEventPasswordAsync, getAllVDKEventSortAndFilterAsync, updateVDKEventLinkAndPasswordAsync, getVDKEventByIdForRegistrationAsync, getPartnerEventsAsync, LinkVDKEventAsync, getAllVDKEventsForTaskAsync, getVDKEventUserCountryAsync } from './VDKEventThunk';
const initialState = {
    status: APIStatus.IDLE,
    isEventValid: false,
    isLinkAndPasswordUpdated: false,
    userCountryStatus: APIStatus.IDLE
};
export const VDKEventSlice = createSlice({
    name: 'VDKEvent',
    initialState,
    reducers: {
        resetEventValidation: (state) => {
            state.isEventValid = false;
            state.passwordValidation = undefined;
        },
        setEventValidation: (state, action) => {
            state.isEventValid = action.payload;
        },
        resetUserCountry: (state) => {
            state.userCountryCode = undefined;
            state.userCountryStatus = APIStatus.IDLE;
        },
        resetVDKEvent: (state) => {
            state.VDKEvent = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createVDKEventAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createVDKEventAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEvent = action.payload;
        })
            .addCase(createVDKEventAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(updateVDKEventAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateVDKEventAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEvent = action.payload;
        })
            .addCase(updateVDKEventAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getAllVDKEventsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllVDKEventsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const payload = action.payload;
            const meta = action.meta;
            const request = meta?.arg;
            // Handle PagedVDKEvents for paginated requests
            if (request?.pageNumber !== undefined) {
                // If pageNumber > 1, append to existing events; otherwise replace
                if (request.pageNumber > 1 && state.PagedVDKEvents) {
                    // Append new events, avoiding duplicates
                    const existingIds = new Set(state.PagedVDKEvents.items.map(e => e.id));
                    const newEvents = payload.items.filter(e => !existingIds.has(e.id));
                    state.PagedVDKEvents = {
                        ...payload,
                        items: [...state.PagedVDKEvents.items, ...newEvents]
                    };
                }
                else {
                    // First page - replace all events
                    state.PagedVDKEvents = payload;
                }
            }
            else {
                // For backward compatibility: if no pageNumber, update VDKEvents array
                state.VDKEvents = payload.items || [];
            }
        })
            .addCase(getAllVDKEventsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getVDKEventByIdAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getVDKEventByIdAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEvent = action.payload;
        })
            .addCase(getVDKEventByIdAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(deleteVDKEventAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(deleteVDKEventAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const deletedEventId = action.payload.data?.id;
            // Update PagedVDKEvents if it exists
            if (state.PagedVDKEvents) {
                state.PagedVDKEvents = {
                    ...state.PagedVDKEvents,
                    items: state.PagedVDKEvents.items.filter(event => event.id !== deletedEventId),
                    totalCount: Math.max(0, (state.PagedVDKEvents.totalCount || 0) - 1)
                };
            }
            // Update VDKEvents array if it exists (for backward compatibility)
            if (state.VDKEvents) {
                state.VDKEvents = state.VDKEvents.filter(event => event.id !== deletedEventId);
            }
            if (state.VDKEventForPartner) {
                const updatedItems = state.VDKEventForPartner.items.filter(event => event.id !== deletedEventId);
                state.VDKEventForPartner.items = updatedItems;
                if (updatedItems.length !== state.VDKEventForPartner.items.length) {
                    state.VDKEventForPartner.totalCount -= 1;
                }
            }
            if (state.VDKEvent?.id === deletedEventId) {
                state.VDKEvent = undefined;
            }
            if (state.VDKEventsForEventDetails?.id === deletedEventId) {
                state.VDKEventsForEventDetails = undefined;
            }
        })
            .addCase(deleteVDKEventAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getVDKEventByIdForEventDetailsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getVDKEventByIdForEventDetailsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEventsForEventDetails = action.payload;
        })
            .addCase(getVDKEventByIdForEventDetailsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(validateEventPasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(validateEventPasswordAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.passwordValidation = action.payload;
            state.isEventValid = action.payload.isValid;
        })
            .addCase(validateEventPasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
            state.isEventValid = false;
        });
        builder
            .addCase(getAllVDKEventSortAndFilterAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllVDKEventSortAndFilterAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEventsSortAndFilter = action.payload;
        })
            .addCase(getAllVDKEventSortAndFilterAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(updateVDKEventLinkAndPasswordAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateVDKEventLinkAndPasswordAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.isLinkAndPasswordUpdated = action.payload;
        })
            .addCase(updateVDKEventLinkAndPasswordAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getVDKEventByIdForRegistrationAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getVDKEventByIdForRegistrationAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEventForRegistration = action.payload;
        })
            .addCase(getVDKEventByIdForRegistrationAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getAllVDKEventsForTaskAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllVDKEventsForTaskAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEventForTasks = action.payload;
        })
            .addCase(getAllVDKEventsForTaskAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getPartnerEventsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getPartnerEventsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.VDKEventForPartner = action.payload;
        })
            .addCase(getPartnerEventsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(LinkVDKEventAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(LinkVDKEventAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            const updatedPartner = action.payload.data;
            if (updatedPartner) {
                if (state.VDKEventForPartner && state.VDKEventForPartner.items) {
                    state.VDKEventForPartner.items = state.VDKEventForPartner.items.map((partner) => partner.id === updatedPartner.id
                        ? { ...partner, status: updatedPartner.status === 0 ? 'Not Linked' : 'Linked' }
                        : partner);
                }
            }
        })
            .addCase(LinkVDKEventAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        });
        builder
            .addCase(getVDKEventUserCountryAsync.pending, (state) => {
            state.userCountryStatus = APIStatus.PENDING;
        })
            .addCase(getVDKEventUserCountryAsync.fulfilled, (state, action) => {
            state.userCountryStatus = APIStatus.SUCCESS;
            state.userCountryCode = action.payload;
        })
            .addCase(getVDKEventUserCountryAsync.rejected, (state) => {
            state.userCountryStatus = APIStatus.FAILED;
            state.userCountryCode = null;
        });
    }
});
export const { resetEventValidation, setEventValidation, resetUserCountry, resetVDKEvent } = VDKEventSlice.actions;
export default VDKEventSlice.reducer;
